import { Button } from "@/components/ui/button";
import { useT, useTPlural } from "@/lib/i18n";
import type { CheckpointResult, ScenarioDetail } from "@/lib/scenarios";

type ResultBorderlineProps = {
  scenario: ScenarioDetail;
  result: CheckpointResult;
  onAccept: () => void;
  onRepeat: () => void;
};

/**
 * Borderline result (60–79%). Two equal-weight CTAs let the user
 * choose: accept the scenario as completed (with weak-rule debt
 * persisted for later review) OR repeat the weakest topic to push
 * through cleanly.
 *
 * Mockup: `design/hifi/scenarios-extra.jsx` `CheckpointBorderlineScreen`
 * lines ~586–629.
 */
export function ResultBorderline({ scenario, result, onAccept, onRepeat }: ResultBorderlineProps) {
  const t = useT();
  const tPlural = useTPlural();
  const pct = Math.round(result.score * 100);

  // Show weak-rule cards. We try to resolve slug → human title from the
  // scenario's rule list (which carries `title`); fall back to the
  // slug itself if not found (e.g. cross-scenario weak rule).
  const weakRuleCards = result.weakRules.map((slug) => {
    const rule = scenario.rules.find((r) => r.slug === slug);
    const errorCount = result.perItem.filter((p) => !p.correct && p.rulesTested.includes(slug)).length;
    return { slug, title: rule?.title ?? slug, errorCount };
  });

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-4 pb-4">
      <div className="flex flex-col items-center gap-3 pt-5 text-center">
        <ScoreCircle correct={result.correctCount} total={result.totalItems} />
        <div className="font-mono text-[11px] font-bold uppercase tracking-[0.1em]" style={{ color: "var(--scen-fg)" }}>
          {t("skills.result_borderline_title")} · {pct}%
        </div>
        <div className="px-4">
          <h1 className="text-[22px] font-bold leading-[1.25] tracking-[-0.02em] text-text">{scenario.title}</h1>
          <p className="mt-2 text-[13px] leading-[1.5] text-text-2">
            {t("skills.result_borderline_subtitle", {
              correct: String(result.correctCount),
              total: String(result.totalItems),
            })}
          </p>
        </div>
      </div>

      {weakRuleCards.length > 0 && (
        <div className="flex flex-col gap-1.5 px-1">
          <div className="font-mono text-[11px] font-semibold uppercase tracking-wider text-text-3">
            {t("skills.result_borderline_weak")}
          </div>
          {weakRuleCards.map((w) => (
            <div
              key={w.slug}
              className="flex items-center gap-3 rounded-[var(--r-md)] border-[1.5px] px-3.5 py-3"
              style={{ borderColor: "var(--scen-bg2)", background: "var(--scen-bg)" }}
            >
              <div
                className="flex size-9 shrink-0 items-center justify-center rounded-md font-mono text-[11px] font-bold uppercase tracking-wider text-white"
                style={{ background: "var(--scen-solid)" }}
              >
                {t("skills.weak_rule_badge")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-text">{w.title}</div>
                <div className="font-mono text-[11px]" style={{ color: "var(--scen-fg)" }}>
                  {tPlural("skills.weak_rule_errors", w.errorCount)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1" />

      <div className="flex flex-col gap-2 px-1">
        <Button type="button" variant="scenario" size="block" onClick={onAccept}>
          {t("skills.result_borderline_accept")}
        </Button>
        <button
          type="button"
          onClick={onRepeat}
          className="w-full rounded-[var(--r-md)] border border-border bg-[color:var(--surface-2)] py-3 text-[14px] font-medium text-text-2 transition-colors hover:bg-[color:var(--surface-3)]"
        >
          {t("skills.result_borderline_repeat")}
        </button>
      </div>
    </div>
  );
}

function ScoreCircle({ correct, total }: { correct: number; total: number }) {
  const pct = total > 0 ? correct / total : 0;
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - pct * circumference;
  return (
    <div
      className="relative flex size-[72px] items-center justify-center"
      style={{ background: "var(--scen-bg)", color: "var(--scen-fg)", borderRadius: "50%" }}
    >
      <svg viewBox="0 0 80 80" className="absolute inset-0">
        <circle cx="40" cy="40" r="34" fill="none" stroke="var(--scen-bg2)" strokeWidth="4" />
        <circle
          cx="40"
          cy="40"
          r="34"
          fill="none"
          stroke="var(--scen-solid)"
          strokeWidth="4"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 40 40)"
        />
      </svg>
      <span className="relative font-mono text-[16px] font-bold" style={{ color: "var(--scen-ink)" }}>
        {correct}/{total}
      </span>
    </div>
  );
}
